import * as React from "react";
import { useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import { InputAdornment, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import MenuItem from "@material-ui/core/MenuItem";
import { _post } from "../../../Api";
import { useParams } from "react-router-dom";
import GetCurrency from "../../../helper/GetCurrency";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import _ from "lodash";
import DeleteIcon from "@material-ui/icons/Delete";

/**
 *
 * The comments below for once subscriptions apply in a similar fashion for other types of subscriptions.
 * The implementation of methods may differ per subscription type, but the purpose of each method remains the same.
 * Each subscription has the following methods:
 * - openAddDialog -> open adding a subscription dialog
 * - openRemoveDialog -> open removing a subscription dialog
 * - initialize{type}subscription -> initialize subscription of a certain {type}
 * - change{type}subscription -> change a property of a subscription {type}
 * - post{type}subscription -> send a POST request to the server to save the state of a subscription of a certain type
 *
 * Furthermore, there is an if-statement preceding the return (render method) to ensure that all necessary props have been loaded and set-up before rendering
 * When the conditions of this if-statement are true, it renders a loading circle indicating that the page/component is loading
 *
 * Lastly, there is a return (render method) function that renders the component for each type of subscription.
 *
 * TIPS:
 * You can use {(() => {}))} to write a self-executing function within the return method. For example, when a different html-tag/component needs to be
 * loaded dependent on the boolean value of an if-statement. Don't forget return a div when doing this!
 */

export default function OnceSubscriptions(props) {
  // two states are used to check whether the user has changed a value to avoid unnecessary calls to the server
  const [onceSubscriptions, setOnceSubscriptions] = React.useState(null);
  const [editOnceSubscriptions, setEditOnceSubscriptions] = React.useState(null);
  const merchantId = parseInt(useParams().merchantId); // this is used to find the current merchant
  /**
   * editable is from the parent component src/screens/Subscriptions.js
   * editable is true when a user is authorized to edit values for a subscription type, if they are not authorized, editable is false.
   */
  const editable = props.editable;

  // initialize once subscription after rendering the page and re-render afterwards.
  useEffect(initializeOnceSubscriptions, []);

  /**
   * when the user clicks on the '+ subscription' (add subscription) button open the add once dialog
   */
  const openAddDialog = () => props.setAddDialogType("once");

  /**
   * When the user clicks on the 'trash can' to delete a subscription, open the remove dialog
   * @param id: the id of a subscription
   */
  const openRemoveDialog = (id) => {
    props.setSelectedSubscription(id);
    props.setRemoveDialogType("once");
  };

  /**
   * Change a property of a once subscription
   * @param subscription_id: id of a subscription
   * @param key: the property that needs to change
   * @param value: the new value
   */
  function changeOnceSubscription(subscription_id, key, value) {
    if (value == null || editOnceSubscriptions == null) return;
    const subs = _.cloneDeep(editOnceSubscriptions);
    const currentSub = editOnceSubscriptions.findIndex((sub) => sub.id === subscription_id);
    if (key === "wefact_merchant") {
      subs[currentSub]["wefact_merchant"] = props.wefactMerchants.find((merchant) => merchant.id === value);
    } else if (key.includes("meta")) {
      subs[currentSub]["meta"][key.replace("meta ", "")] = value;
    } else {
      subs[currentSub][key] = value;
    }
    setEditOnceSubscriptions(subs);
  }

  /**
   * Initialize the once subscription by filtering the subscriptions from props.subscriptions and setting the state
   */
  function initializeOnceSubscriptions() {
    const onceSubs = props.subscriptions.filter((subscription) => subscription.type === props.merchantType);
    setOnceSubscriptions(_.cloneDeep(onceSubs));
    setEditOnceSubscriptions(_.cloneDeep(onceSubs));
  }

  /**
   * This function sends a post request to the server when the user has clicked outside of a input field (onBlur)
   * @param subscription_id: id of a subscription
   * @param type: the property that has been changed by the user
   */
  function postOnceSubscription(subscription_id, type) {
    const index = editOnceSubscriptions.findIndex((sub) => sub.id === subscription_id);
    const equal = editOnceSubscriptions[index][type]
      ? editOnceSubscriptions[index][type] === onceSubscriptions[index][type]
      : editOnceSubscriptions[index]["meta"][type] === onceSubscriptions[index]["meta"][type];
    if (equal) return;
    let data;
    if (type === "price" || type === "number" || type === "number_suffix") {
      data = {
        meta: {
          price: editOnceSubscriptions[index].meta.price,
          number: editOnceSubscriptions[index].meta.number,
          number_suffix: editOnceSubscriptions[index].meta.number_suffix,
        },
      };
    } else {
      data = {
        [type]: editOnceSubscriptions[index][type],
      };
    }
    _post("/nova/merchant/" + merchantId + "/subscription/" + subscription_id, data)
      .then(() => {
        toast.success("Saved once subscriptions successfully!", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setOnceSubscriptions(editOnceSubscriptions);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Once subscriptions could not be saved.", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
  }

  /**
   * Do not load the page when the subscriptions have not been loaded or when the wefactmerchants are absent.
   */
  if (editOnceSubscriptions == null || props.wefactMerchants == null) {
    return (
      <div align="center">
        <CircularProgress color="primary" />
      </div>
    );
  }

  /**
   * Render the final page
   */
  return (
    <Grid container direction="column" spacing={4}>
      <Grid container direction="row" spacing={2}>
        <Grid item>
          <Typography variant={"h5"}>Once</Typography>
        </Grid>

        <Grid item>
          <Button
            variant="contained"
            disabled={!editable}
            onClick={openAddDialog}
            startIcon={<AddIcon />}
            color="primary"
          >
            Subscription
          </Button>
        </Grid>
      </Grid>
      <Divider style={{ margin: "20px 0" }} />
      {editOnceSubscriptions.map((subscription) => {
        if (editable) {
          return (
            <Grid item key={subscription.id}>
              <Card style={{ padding: "1em" }}>
                <Grid container direction={"row"} spacing={3}>
                  <Grid item>
                    <FormControl>
                      <InputLabel>debtor</InputLabel>
                      <Select
                        value={subscription.wefact_merchant_id}
                        onBlur={() => postOnceSubscription(subscription.id, "wefact_merchant")}
                        onChange={(event) =>
                          changeOnceSubscription(subscription.id, "wefact_merchant", event.target.value)
                        }
                      >
                        {props.wefactMerchants.map((merchant) => {
                          return (
                            <MenuItem value={merchant.id} key={merchant.id}>
                              {merchant.debtor_code + " " + merchant.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="title"
                      value={subscription.name}
                      onBlur={() => postOnceSubscription(subscription.id, "name")}
                      onChange={(event) => changeOnceSubscription(subscription.id, "name", event.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="date"
                      type="date"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onChange={(event) =>
                        changeOnceSubscription(subscription.id, "subscription_started", event.target.value)
                      }
                      onBlur={() => postOnceSubscription(subscription.id, "subscription_started")}
                      value={(() => {
                        const index = subscription.subscription_started.indexOf("T");
                        return index > -1
                          ? subscription.subscription_started.slice(0, index)
                          : subscription.subscription_started;
                      })()}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined">
                      <InputLabel>number</InputLabel>
                      <OutlinedInput
                        type="number"
                        labelWidth={60}
                        inputMode="numeric"
                        onBlur={() => postOnceSubscription(subscription.id, "number")}
                        onChange={(event) => changeOnceSubscription(subscription.id, "meta number", event.target.value)}
                        value={subscription.meta.number}
                        inputProps={{
                          min: 0,
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="type"
                      type="text"
                      onChange={(event) =>
                        changeOnceSubscription(subscription.id, "meta number_suffix", event.target.value)
                      }
                      onBlur={() => postOnceSubscription(subscription.id, "number_suffix")}
                      value={subscription.meta.number_suffix}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined">
                      <InputLabel>price</InputLabel>
                      <OutlinedInput
                        type="number"
                        inputMode="decimal"
                        startAdornment={<InputAdornment position="start">{GetCurrency()}</InputAdornment>}
                        labelWidth={30}
                        onBlur={() => postOnceSubscription(subscription.id, "price")}
                        onChange={(event) => changeOnceSubscription(subscription.id, "meta price", event.target.value)}
                        value={subscription.meta.price}
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <Paper variant="elevation" style={{ padding: "1em" }}>
                      <Typography variant="h6">
                        {subscription.meta.number +
                          " " +
                          subscription.meta.number_suffix +
                          " " +
                          GetCurrency() +
                          " " +
                          subscription.meta.price}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item>
                    <Button startIcon={<DeleteIcon />} onClick={() => openRemoveDialog(subscription.id)} />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          );
        } else {
          return (
            <Grid item key={subscription.id}>
              <Card style={{ padding: "1em" }}>
                <Grid container direction={"row"} spacing={3}>
                  <Grid item>
                    <FormControl disabled>
                      <InputLabel>debtor</InputLabel>
                      <Select
                        value={subscription.wefact_merchant_id}
                        onBlur={() => postOnceSubscription(subscription.id, "wefact_merchant")}
                        onChange={(event) =>
                          changeOnceSubscription(subscription.id, "wefact_merchant", event.target.value)
                        }
                      >
                        {props.wefactMerchants.map((merchant) => {
                          return (
                            <MenuItem value={merchant.id} key={merchant.id}>
                              {merchant.debtor_code + " " + merchant.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="title"
                      value={subscription.name}
                      disabled
                      onBlur={() => postOnceSubscription(subscription.id, "name")}
                      onChange={(event) => changeOnceSubscription(subscription.id, "name", event.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      label="date"
                      type="date"
                      disabled
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onChange={(event) =>
                        changeOnceSubscription(subscription.id, "subscription_started", event.target.value)
                      }
                      onBlur={() => postOnceSubscription(subscription.id, "subscription_started")}
                      value={(() => {
                        const index = subscription.subscription_started.indexOf("T");
                        return index > -1
                          ? subscription.subscription_started.slice(0, index)
                          : subscription.subscription_started;
                      })()}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined" disabled>
                      <InputLabel>number</InputLabel>
                      <OutlinedInput
                        type="number"
                        labelWidth={60}
                        inputMode="numeric"
                        onBlur={() => postOnceSubscription(subscription.id, "number")}
                        onChange={(event) => changeOnceSubscription(subscription.id, "meta number", event.target.value)}
                        value={subscription.meta.number}
                        inputProps={{
                          min: 0,
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="type"
                      type="text"
                      disabled
                      onChange={(event) =>
                        changeOnceSubscription(subscription.id, "meta number_suffix", event.target.value)
                      }
                      onBlur={() => postOnceSubscription(subscription.id, "number_suffix")}
                      value={subscription.meta.number_suffix}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined" disabled>
                      <InputLabel>price</InputLabel>
                      <OutlinedInput
                        type="number"
                        inputMode="decimal"
                        startAdornment={<InputAdornment position="start">{GetCurrency()}</InputAdornment>}
                        labelWidth={30}
                        onBlur={() => postOnceSubscription(subscription.id, "price")}
                        onChange={(event) => changeOnceSubscription(subscription.id, "meta price", event.target.value)}
                        value={subscription.meta.price}
                        inputProps={{
                          min: 0,
                          step: 0.01,
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <Paper variant="elevation" style={{ padding: "1em" }}>
                      <Typography variant="h6">
                        {subscription.meta.number +
                          " " +
                          subscription.meta.number_suffix +
                          " " +
                          GetCurrency() +
                          " " +
                          subscription.meta.price}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item>
                    <Button disabled startIcon={<DeleteIcon />} onClick={() => openRemoveDialog(subscription.id)} />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          );
        }
      })}
    </Grid>
  );
}
